if (!customElements.get('pdp-bundle-logic')) {
  class PdpBundleLogic extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleSheetTouchStart = this.handleSheetTouchStart.bind(this);
      this.handleSheetTouchMove = this.handleSheetTouchMove.bind(this);
      this.handleSheetTouchEnd = this.handleSheetTouchEnd.bind(this);
      this.selectedProductIndex = null;
      this.selectedBundleIndex = null;
      this.sheetDrag = null;
    }

    connectedCallback() {
      this.cacheDom();
      this.syncModalThemeVars();
      this.mountModals();
      this.initializeSelection();
      this.bindEvents();
      this.updateSelectionState();
      this.updateDerivedState();
      this.syncCardHeights();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      this.getManagedModals().forEach((modal) => {
        modal.removeEventListener('click', this.handleClick);
        modal.removeEventListener('transitionend', this.handleTransitionEnd);
        modal.removeEventListener('touchstart', this.handleSheetTouchStart);
        modal.removeEventListener('touchmove', this.handleSheetTouchMove);
        modal.removeEventListener('touchend', this.handleSheetTouchEnd);
        modal.removeEventListener('touchcancel', this.handleSheetTouchEnd);
      });
    }

    cacheDom() {
      this.productsContainer = this.querySelector('[data-products]');
      this.selectionModalAnchor = this.querySelector('[data-selection-modal-anchor]');
      this.selectionModal = this.querySelector('[data-selection-modal]');
      this.accessoryModal = this.querySelector('[data-accessory-modal]');
      this.popupImage = this.querySelector('[data-popup-image]');
      this.popupTitle = this.querySelector('[data-popup-title]');
      this.popupDescription = this.querySelector('[data-popup-description]');
      this.hasManyBlocks = this.querySelectorAll('.bundle-product-block').length > 3;
      if (this.hasManyBlocks) this.classList.add('has-many-blocks');
      this.cacheSelectionDom();
    }

    cacheSelectionDom() {
      const selectionScope = this.getSelectionScope();
      this.filterList = selectionScope.querySelector('[data-filter-list]');
      this.addToCartButton = selectionScope.querySelector('[data-action="bundle-add-to-cart"]');
      this.addToCartText = selectionScope.querySelector('[data-cta-text]');
      this.viewProductLink = selectionScope.querySelector('[data-view-product-link]');
      this.sizeTriggerText = selectionScope.querySelector('.bundle-logic__size-trigger-text');
      this.previewImage = selectionScope.querySelector('[data-preview-image]');
      this.previewSaleLabel = selectionScope.querySelector('[data-preview-sale-label]');
      this.previewCustomLabel = selectionScope.querySelector('[data-preview-custom-label]');
      this.compareTitle = selectionScope.querySelector('[data-compare-title]');
      this.compareCount = selectionScope.querySelector('[data-compare-count]');
      this.compareSection = selectionScope.querySelector('.bundle-logic__compare');
      this.mainSection = selectionScope.querySelector('.bundle-logic__main');
      this.selectionContent = selectionScope.querySelector('.bundle-logic__selection-content');
      this.selectionCard = selectionScope.querySelector('.bundle-logic__selection-card');
    }

    bindEvents() {
      this.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
      this.getManagedModals().forEach((modal) => {
        modal.addEventListener('click', this.handleClick);
        modal.addEventListener('transitionend', this.handleTransitionEnd);
        modal.addEventListener('touchstart', this.handleSheetTouchStart, { passive: true });
        modal.addEventListener('touchmove', this.handleSheetTouchMove, { passive: false });
        modal.addEventListener('touchend', this.handleSheetTouchEnd);
        modal.addEventListener('touchcancel', this.handleSheetTouchEnd);
      });
    }

    mountModals() {
      this.syncModalThemeVars();
      this.placeSelectionModal();

      if (!this.accessoryModal) return;
      if (!this.accessoryModal.dataset.modalHostId) {
        this.accessoryModal.dataset.modalHostId = this.id || `bundle-logic-${Math.random().toString(36).slice(2, 10)}`;
        if (!this.id) this.id = this.accessoryModal.dataset.modalHostId;
      }
      document.body.appendChild(this.accessoryModal);
      this.syncModalThemeVars();
    }

    handleResize() {
      this.syncModalThemeVars();
      const wasMobile = this.isMobileView;
      this.isMobileView = window.matchMedia('(max-width: 767px)').matches;
      this.placeSelectionModal();
      this.cacheSelectionDom();

      if (wasMobile !== this.isMobileView) {
        this.initializeSelection();
        this.updateSelectionState();
        this.updateDerivedState();
      }

      if (this.selectionModal && !this.isMobileView) {
        this.selectionModal.setAttribute('aria-hidden', 'true');
        this.selectionModal.classList.remove('is-open', 'is-closing');
        const accessoryModalOpen = this.accessoryModal && this.accessoryModal.getAttribute('aria-hidden') === 'false';
        document.body.classList.toggle('bundle-logic-modal-open', Boolean(accessoryModalOpen));
      }

      this.syncCardHeights();
    }

    handleClick(event) {
      const target = event.target.closest('[data-action], [data-product-index], [data-bundle-index], [data-accessory]');
      if (!target) return;

      if (target.dataset.bundleIndex) {
        event.preventDefault();
        this.selectBundle(parseInt(target.dataset.bundleIndex, 10));
        return;
      }

      if (target.dataset.productIndex) {
        event.preventDefault();
        this.selectProduct(parseInt(target.dataset.productIndex, 10));
        this.openSelectionModal();
        return;
      }

      if (target.dataset.accessory) {
        event.preventDefault();
        this.openAccessoryModal(JSON.parse(target.dataset.accessory));
        return;
      }

      switch (target.dataset.action) {
        case 'close-accessory-modal':
          this.toggleModal(this.accessoryModal, false);
          if (this.selectionModal) this.selectionModal.classList.remove('overlay-hidden');
          break;
        case 'close-selection-modal':
          this.toggleModal(this.selectionModal, false);
          break;
        case 'bundle-add-to-cart':
          this.addSelectedBundleToCart();
          break;
      }
    }

    selectProduct(index) {
      if (Number.isNaN(index)) return;
      this.selectedProductIndex = index;
      this.selectedBundleIndex = 0;
      this.updateSelectionState();

      this.toggleProductCards(this.productsContainer, index);
      this.toggleFilterItems(this.filterList, index);

      this.getBundleGroups().forEach((group) => {
        group.hidden = group.dataset.productBundles !== String(index);
      });

      this.getBundleGroups().forEach((group) => {
        group.querySelectorAll('[data-bundle-index]').forEach((button, buttonIndex) => {
          button.classList.toggle('is-active', !group.hidden && buttonIndex === 0);
        });
      });

      this.updateDerivedState();
    }

    selectBundle(index) {
      if (Number.isNaN(index) || this.selectedProductIndex === null) return;
      this.selectedBundleIndex = index;

      const activeGroup = this.getBundleGroup(this.selectedProductIndex);
      if (activeGroup) {
        activeGroup.querySelectorAll('[data-bundle-index]').forEach((button) => {
          button.classList.toggle('is-active', button.dataset.bundleIndex === String(index));
        });
      }

      this.updateDerivedState();
    }

    updateDerivedState() {
      if (this.selectedProductIndex === null || this.selectedBundleIndex === null) {
        return;
      }

      const activeGroup = this.getBundleGroup(this.selectedProductIndex);
      const hasBundles = activeGroup ? activeGroup.querySelectorAll('[data-bundle-index]').length > 0 : false;
      if (this.mainSection) this.mainSection.hidden = !hasBundles;

      this.updateBundlePreview();
      this.updateComparePanels();
      this.updateCtas();
      this.syncCardHeights();
    }

    updateSelectionState() {
      const hasSelection = this.selectedProductIndex !== null && this.selectedBundleIndex !== null;
      this.classList.toggle('has-selection', hasSelection);
    }

    toggleFilterItems(container, index) {
      if (!container) return;
      container.querySelectorAll('[data-product-index]').forEach((item) => {
        const isActive = item.dataset.productIndex === String(index);
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    updateBundlePreview() {
      const activeBundleButton = this.getActiveBundleButton();
      if (!activeBundleButton) return;

      if (this.previewImage && activeBundleButton.dataset.imageSrc) {
        const nextSrc = activeBundleButton.dataset.imageSrc;
        if (this.previewImage.getAttribute('src') !== nextSrc) {
          this.previewImage.setAttribute('src', nextSrc);
        }
        this.previewImage.setAttribute('alt', activeBundleButton.dataset.imageAlt || '');
      }

      if (this.previewSaleLabel) {
        const saleText = activeBundleButton.dataset.saleLabel || '';
        this.previewSaleLabel.textContent = saleText;
        this.previewSaleLabel.hidden = !saleText;
      }

      if (this.previewCustomLabel) {
        const customText = activeBundleButton.dataset.customLabel || '';
        this.previewCustomLabel.textContent = customText;
        this.previewCustomLabel.hidden = !customText;
        if (customText) {
          this.previewCustomLabel.style.color = activeBundleButton.dataset.customLabelColor || '';
          this.previewCustomLabel.style.background = activeBundleButton.dataset.customLabelBg || '';
        }
      }
    }

    updateComparePanels() {
      const key = `${this.selectedProductIndex}-${this.selectedBundleIndex}`;
      const activeBundleButton = this.getActiveBundleButton();

      this.getComparePanels().forEach((panel) => {
        panel.hidden = panel.dataset.comparePanel !== key;
      });

      const activePanel = this.getComparePanel(key);
      if (this.compareCount && activePanel) {
        const count = activePanel.querySelectorAll('.bundle-logic__compare-item--included').length;
        const template = this.compareCount.dataset.productsCountTemplate || `(${count} products)`;
        this.compareCount.textContent = template.replace('__COUNT__', count);
      }

      if (this.compareSection) {
        const hasItems = activePanel
          ? activePanel.querySelector('.bundle-logic__compare-grid')?.children.length > 0
          : false;
        this.compareSection.hidden = !hasItems;
      }

      if (this.compareTitle && activeBundleButton) {
        const bundleTitle = activeBundleButton.querySelector('.bundle-logic__bundle-title');
        const comparisonLabel = this.compareTitle.dataset.comparisonTitle || 'Included';
        const inConnector = this.compareTitle.dataset.inConnector || 'in';
        this.compareTitle.textContent = bundleTitle
          ? `${comparisonLabel} ${inConnector} ${bundleTitle.textContent.trim()}`
          : comparisonLabel;
      }
    }

    updateCtas() {
      const activeBundleButton = this.getActiveBundleButton();
      if (!activeBundleButton) return;

      if (this.addToCartButton) {
        const isAvailable = activeBundleButton.dataset.available === 'true';
        this.addToCartButton.dataset.variantId = activeBundleButton.dataset.variantId || '';
        this.addToCartButton.disabled = !isAvailable;

        if (this.addToCartText) {
          this.addToCartText.textContent = isAvailable
            ? (this.addToCartText.dataset.addToCartText || 'Add to cart')
            : (this.addToCartText.dataset.outOfStockText || 'Out of Stock');
        }
      }

      if (this.viewProductLink) {
        this.viewProductLink.href = activeBundleButton.dataset.url || this.viewProductLink.href;
      }
    }

    getActiveBundleButton() {
      if (this.selectedProductIndex === null || this.selectedBundleIndex === null) return null;
      const activeGroup = this.getBundleGroup(this.selectedProductIndex);
      return activeGroup
        ? activeGroup.querySelector(`[data-bundle-index="${this.selectedBundleIndex}"]`)
        : null;
    }

    toggleProductCards(container, index) {
      if (!container) return;
      container.querySelectorAll('[data-product-index]').forEach((card) => {
        card.classList.toggle('is-active', card.dataset.productIndex === String(index));
      });
    }

    toggleModal(modal, isOpen) {
      if (!modal) return;

      if (isOpen) {
        clearTimeout(modal._closeTimer);
        modal.classList.remove('is-closing');
        modal.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          modal.classList.add('is-open');
        });
        document.body.classList.add('bundle-logic-modal-open');
        return;
      }

      modal.classList.remove('is-open');
      modal.classList.add('is-closing');
      this.updateBodyModalState();

      clearTimeout(modal._closeTimer);
      modal._closeTimer = setTimeout(() => {
        if (modal.classList.contains('is-closing')) {
          modal.classList.remove('is-closing');
          modal.setAttribute('aria-hidden', 'true');
          this.updateBodyModalState();
        }
      }, 400);
    }

    handleTransitionEnd(event) {
      const modal = event.target.closest('[data-accessory-modal], [data-selection-modal]');
      if (!modal || !modal.classList.contains('is-closing')) return;

      modal.classList.remove('is-closing');
      modal.setAttribute('aria-hidden', 'true');
      this.updateBodyModalState();
    }

    handleSheetTouchStart(event) {
      if (!this.canStartSelectionSheetDrag(event)) return;

      const card = this.getSelectionCard();
      if (!card || card.scrollTop > 0) return;

      const touch = event.touches[0];
      if (!touch) return;

      this.sheetDrag = {
        startY: touch.clientY,
        currentY: touch.clientY,
        dragging: false,
        card
      };
    }

    handleSheetTouchMove(event) {
      if (!this.sheetDrag) return;

      const touch = event.touches[0];
      if (!touch) return;

      const deltaY = touch.clientY - this.sheetDrag.startY;
      this.sheetDrag.currentY = touch.clientY;

      if (deltaY <= 0) return;
      if (!this.sheetDrag.dragging && this.sheetDrag.card.scrollTop > 0) return;

      if (deltaY > 8) {
        this.sheetDrag.dragging = true;
        this.sheetDrag.card.classList.add('is-dragging');
      }

      if (!this.sheetDrag.dragging) return;

      event.preventDefault();
      this.sheetDrag.card.style.transform = `translate3d(0, ${deltaY}px, 0)`;
    }

    handleSheetTouchEnd() {
      if (!this.sheetDrag) return;

      const { card, startY, currentY, dragging } = this.sheetDrag;
      const deltaY = Math.max(currentY - startY, 0);
      const closeThreshold = Math.min(150, window.innerHeight * 0.2);

      card.classList.remove('is-dragging');
      card.style.transform = '';

      if (dragging && deltaY > closeThreshold) {
        this.toggleModal(this.selectionModal, false);
      }

      this.sheetDrag = null;
    }

    canStartSelectionSheetDrag(event) {
      if (!this.selectionModal || this.selectionModal.getAttribute('aria-hidden') === 'true') return false;
      if (!window.matchMedia('(max-width: 767px)').matches) return false;
      const isDragArea = event.target.closest('.bundle-logic__selection-content, .bundle-logic__modal-header');
      if (!isDragArea) return false;
      if (event.target.closest('a, button, input, select, textarea, [role="button"]')) {
        return Boolean(event.target.closest('.bundle-logic__modal-header'));
      }
      return true;
    }

    getSelectionCard() {
      return this.selectionCard || this.selectionModal?.querySelector('.bundle-logic__selection-card');
    }

    updateBodyModalState() {
      const anyModalOpen = this.getManagedModals()
        .some((element) => element.classList.contains('is-open') || element.classList.contains('is-closing'));
      document.body.classList.toggle('bundle-logic-modal-open', anyModalOpen);
    }

    initializeSelection() {
      this.isMobileView = window.matchMedia('(max-width: 767px)').matches;

      if (this.isMobileView && !this.hasManyBlocks) {
        this.selectedProductIndex = null;
        this.selectedBundleIndex = null;
        this.toggleProductCards(this.productsContainer, null);
        this.toggleFilterItems(this.filterList, null);
        this.getBundleGroups().forEach((group) => {
          group.hidden = true;
          group.querySelectorAll('[data-bundle-index]').forEach((button) => {
            button.classList.remove('is-active');
          });
        });
        this.getComparePanels().forEach((panel) => {
          panel.hidden = true;
        });
        return;
      }

      this.selectedProductIndex = this.getActiveIndex('[data-products] [data-product-index]');
      this.selectedBundleIndex = this.getActiveIndex('[data-product-bundles]:not([hidden]) [data-bundle-index]');

      if (this.selectedProductIndex === null) {
        this.selectedProductIndex = 0;
      }

      if (this.selectedBundleIndex === null) {
        this.selectedBundleIndex = 0;
      }

      this.toggleProductCards(this.productsContainer, this.selectedProductIndex);
      this.toggleFilterItems(this.filterList, this.selectedProductIndex);

      this.getBundleGroups().forEach((group) => {
        const isActiveGroup = group.dataset.productBundles === String(this.selectedProductIndex);
        group.hidden = !isActiveGroup;
        group.querySelectorAll('[data-bundle-index]').forEach((button, buttonIndex) => {
          button.classList.toggle('is-active', isActiveGroup && buttonIndex === this.selectedBundleIndex);
        });
      });
    }

    getManagedModals() {
      return [this.selectionModal, this.accessoryModal].filter(Boolean);
    }

    placeSelectionModal() {
      if (!this.selectionModal) return;

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const targetParent = (isMobile && !this.hasManyBlocks)
        ? document.body
        : this.selectionModalAnchor;

      if (!targetParent || this.selectionModal.parentNode === targetParent) return;
      targetParent.appendChild(this.selectionModal);
      this.syncModalThemeVars();
      this.cacheSelectionDom();
    }

    syncModalThemeVars() {
      const themeVars = [
        '--bundle-logic-section-bg',
        '--bundle-logic-card-bg',
        '--bundle-logic-text-primary',
        '--bundle-logic-text-secondary',
        '--bundle-logic-active-bg',
        '--bundle-logic-line',
        '--bundle-logic-line-hover',
        '--bundle-logic-cta-bg',
        '--bundle-logic-cta-text',
        '--bundle-logic-cta-hover-bg',
        '--bundle-logic-cta-hover-text',
        '--bundle-logic-cta-hover-icon',
        '--bundle-logic-cart-text',
        '--bundle-logic-cart-bg',
        '--bundle-logic-cart-hover-text',
        '--bundle-logic-cart-hover-bg',
        '--bundle-logic-cart-hover-icon',
        '--bundle-logic-view-product-text',
        '--bundle-logic-view-product-bg',
        '--bundle-logic-view-product-hover-text',
        '--bundle-logic-view-product-hover-bg',
        '--bundle-logic-view-product-hover-icon'
          ];

      const sourceStyles = getComputedStyle(this);

      this.getManagedModals().forEach((modal) => {
        themeVars.forEach((name) => {
          const value = sourceStyles.getPropertyValue(name).trim();
          if (value) {
            modal.style.setProperty(name, value);
          }
        });
      });
    }

    getSelectionScope() {
      return this.selectionModal || this;
    }

    getBundleGroups() {
      return Array.from(this.getSelectionScope().querySelectorAll('[data-product-bundles]'));
    }

    getBundleGroup(productIndex) {
      return this.getSelectionScope().querySelector(`[data-product-bundles="${productIndex}"]`);
    }

    getComparePanels() {
      return Array.from(this.getSelectionScope().querySelectorAll('[data-compare-panel]'));
    }

    getComparePanel(key) {
      return this.getSelectionScope().querySelector(`[data-compare-panel="${key}"]`);
    }

    openSelectionModal() {
      if (!this.selectionModal || !window.matchMedia('(max-width: 767px)').matches) return;
      if (this.querySelectorAll('.bundle-product-block').length > 3) return;
      this.toggleModal(this.selectionModal, true);
    }

    openAccessoryModal(item) {
      this.popupImage.innerHTML = item.image ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : '';
      this.popupTitle.textContent = item.title || '';
      this.popupDescription.innerHTML = item.description || '';
      if (this.selectionModal) this.selectionModal.classList.add('overlay-hidden');
      this.toggleModal(this.accessoryModal, true);
    }

    async addSelectedBundleToCart() {
      if (!this.addToCartButton || !this.addToCartButton.dataset.variantId) return;

      const variantId = this.addToCartButton.dataset.variantId;
      const originalText = this.addToCartButton.textContent;
      const addingText = (this.addToCartText && this.addToCartText.dataset.addingText) || 'Adding...';
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = addingText;

      try {
        const response = await fetch(`${window.routes.localeRootUrl}/cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        this.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variants: [variantId],
            total_quantity: 1
          }
        }));
      } catch (error) {
        this.dispatchEvent(new CustomEvent('product:error', { bubbles: true }));
      } finally {
        this.addToCartButton.textContent = originalText;
        this.updateCtas();
      }
    }

    syncCardHeights() {
      const cards = this.querySelectorAll('.bundle-logic__product-card');
      let maxHeight = 0;

      cards.forEach((card) => {
        card.style.minHeight = '';
      });

      cards.forEach((card) => {
        maxHeight = Math.max(maxHeight, card.offsetHeight);
      });

      cards.forEach((card) => {
        // card.style.minHeight = `${maxHeight}px`;
      });
    }

    getActiveIndex(selector) {
      const activeElement = this.querySelector(`${selector}.is-active`);
      if (!activeElement) return null;

      const rawIndex = activeElement.dataset.productIndex ?? activeElement.dataset.bundleIndex;
      const parsedIndex = parseInt(rawIndex, 10);
      return Number.isNaN(parsedIndex) ? null : parsedIndex;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('pdp-bundle-logic', PdpBundleLogic);
}
